/**
 * Design tokens — the single source of truth for the Mini App look (spec 4).
 *
 * The palette lives as CSS variables (RGB channels) on :root and :root.dark in
 * styles/index.css; Tailwind reads them by name (tailwind.config.ts). The hex
 * values below mirror those variables for places that need a plain colour
 * (Telegram's own bars, <meta name="theme-color">).
 *
 * The accent is set in three variables: --accent, --on-accent, --accent-soft.
 */
import type { Theme } from '../lib/theme';

type PaletteName =
  | 'bg'
  | 'surface'
  | 'surface2'
  | 'text'
  | 'hint'
  | 'line'
  | 'accent'
  | 'onAccent'
  | 'accentSoft'
  | 'onAccentSoft'
  | 'ink'
  | 'onInk'
  | 'success'
  | 'successSoft'
  | 'warning'
  | 'warningSoft'
  | 'danger'
  | 'dangerSoft';

export const palette: Record<Theme, Record<PaletteName, string>> = {
  light: {
    bg: '#F2F1F6',
    surface: '#FFFFFF',
    surface2: '#E6E5EC',
    text: '#141414',
    hint: '#77777F',
    line: '#E3E2E8',
    accent: '#C6F432',
    onAccent: '#141414',
    accentSoft: '#F1FBD2',
    onAccentSoft: '#141414',
    ink: '#141414',
    onInk: '#C6F432',
    success: '#15803D',
    successSoft: '#DCFCE7',
    warning: '#B45309',
    warningSoft: '#FEF3C7',
    danger: '#DC2626',
    dangerSoft: '#FEE2E2',
  },
  dark: {
    bg: '#0E0E10',
    surface: '#1A1A1E',
    surface2: '#242429',
    text: '#F4F4F5',
    hint: '#8D8D96',
    line: '#2C2C32',
    accent: '#C6F432',
    onAccent: '#141414',
    accentSoft: '#2A3510',
    onAccentSoft: '#C6F432',
    ink: '#F4F4F5',
    onInk: '#141414',
    success: '#4ADE80',
    successSoft: '#12301D',
    warning: '#FBBF24',
    warningSoft: '#33270A',
    danger: '#F87171',
    dangerSoft: '#3A1414',
  },
};

/** Page background of a theme: Telegram's header, background and bottom bars take it. */
export function themeBg(theme: Theme): string {
  return palette[theme].bg;
}

export const radius = {
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  full: '9999px',
} as const;

export const spacing = {
  px: '1px',
  '0.5': '2px',
  '1': '4px',
  '2': '8px',
  '3': '12px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '8': '32px',
  '10': '40px',
  '12': '48px',
  '16': '64px',
} as const;

export const typography = {
  fontFamily: {
    sans: [
      "'Onest'",
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'sans-serif',
    ].join(', '),
  },
  fontSize: {
    xs: ['12px', { lineHeight: '16px' }],
    sm: ['14px', { lineHeight: '20px' }],
    base: ['16px', { lineHeight: '24px' }],
    lg: ['18px', { lineHeight: '26px' }],
    xl: ['22px', { lineHeight: '28px' }],
    '2xl': ['28px', { lineHeight: '34px' }],
    '3xl': ['36px', { lineHeight: '42px' }],
  },
  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },
} as const;

export const motion = {
  durationFast: '150ms',
  duration: '200ms',
  durationSlow: '300ms',
  easing: 'cubic-bezier(0.2, 0, 0, 1)', // ease-out, native-feeling
} as const;

export const iconSize = {
  sm: 16,
  md: 20,
  lg: 24,
} as const;
